import {areSetsEqual, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
  ModalMetadataEntryType,
} from 'constants/AnalyticsConstants';
import {SampleControlAttributes, SampleRoleParty, SampleStatusItem} from 'constants/SampleStatusConstants';
import SampleStatusTable from 'inventory/sample-status/SampleStatusTable';
import {checkForSampleStatusWarnings} from 'inventory/sample-status/SampleStatusWarning';
import CurrentAndNewValueRow from 'samples/toolbar/CurrentAndNewValueRow';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import HorizontalChipListField from 'ui-library/chips/HorizontalChipListField';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import ErrorMessage from 'ui-library/ErrorMessage';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import Modal from 'ui-library/modals/Modal';
import PartyList from 'ui-library/PartyList';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {capitalize} from 'util/NameUtil';

export interface ItemWithSampleRoles extends SampleControlAttributes {
  id: string;
}

export interface ItemWithSampleRolesAndPermissions extends ItemWithSampleRoles {
  permissions?: Permissions;
}

export enum BulkUpdateSampleRolesAction {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  REPLACE = 'REPLACE',
  CLEAR = 'CLEAR',
}

const MULTIPLE_VALUES = 'MULTIPLE_VALUES';

export interface BaseBulkUpdateSampleRolesModalProps {
  items: Array<ItemWithSampleRoles>;
  onRequestClose: () => void;
  children?: React.ReactNode;
  warning?: React.ReactNode;
  analyticsMetadata?: AnalyticsMetadataForContext;

  // Setting to true renders the status table to allow de-selecting items. Sample status warnings do not apply
  // and are not shown for this flow; any permissions errors will be surfaced on submit
  shouldAlwaysShowSampleStatusTable?: boolean;
}

interface BulkUpdateSampleRolesModalProps extends BaseBulkUpdateSampleRolesModalProps {
  label: string;
  onRequestSubmit: (
    selectedItems: Array<ItemWithSampleRoles>,
    action: BulkUpdateSampleRolesAction,
    partyIdsByItemId: {[itemId: string]: Set<string>}
  ) => void;
  getNoticeFn?: (
    selectedSampleIds: Array<string>
  ) => {header?: string | null; content?: string | null} | undefined;
  getItemValueFn: (item: ItemWithSampleRoles) => Array<SampleRoleParty>;

  // Note that this function will be called on mount
  onChangeAction?: (newAction: BulkUpdateSampleRolesAction) => void;

  /**
   * Normally the modal lets the user select a group of users and teams, to
   * either add to, remove from, or replace the existing sample roles for each
   * container. When `props.alternateParties` is provided, the user also has the
   * option of selecting the alternate parties radio button, which is labeled
   * according to the label provided in the prop. Selecting it tells the modal to
   * use the parties in `props.alternateParties` to update the sample roles instead.
   */
  alternateParties?: {
    label: string;
    partyIdsByItemId: {[itemId: string]: Set<string>};
    disabled?: boolean;
    analyticsMetadataEntryType: ModalMetadataEntryType;
  };
  // Whether to allow BenchlingApps. We currently support for RSUs, but not SOs.
  allowBenchlingApps?: boolean;
}

/**
 * Do not use directly, use BulkUpdateSampleOwnersModal / BulkUpdateRestrictedSampleUsersModal
 */
export default function BulkUpdateSampleRolesModal(props: BulkUpdateSampleRolesModalProps): JSX.Element {
  const [action, setAction] = useState<BulkUpdateSampleRolesAction>(BulkUpdateSampleRolesAction.ADD);
  const [parties, setParties] = useState<Array<MinimalEntity>>([]);
  const [partiesError, setPartiesError] = useState<string | null>(null);
  const [useAlternateParties, setUseAlternateParties] = useState<boolean>(
    props.alternateParties != null && !props.alternateParties.disabled
  );
  const analyticsMetadata = {
    ...{actionOrigin: ActionOrigin.UI_UNDETERMINED},
    ...(props.analyticsMetadata ?? {}),
  };

  // for rendering SampleStatusWarningTable
  const [samples, setSamples] = useState<Array<SampleStatusItem> | null>(null);

  // initialize selected samples to be all samples by default
  const initialSelectedSampleIds = props.items.map(({id}) => id);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Array<string>>(initialSelectedSampleIds);
  const selectedItems = props.items.filter(({id}) => selectedSampleIds.includes(id));
  const currentValues = determineCurrentValues(selectedItems.map((item) => props.getItemValueFn(item)));

  useOnMount(() => {
    if (props.shouldAlwaysShowSampleStatusTable) {
      checkForSampleStatusWarnings({
        sampleIds: initialSelectedSampleIds,
        shouldAlwaysShowWarning: props.shouldAlwaysShowSampleStatusTable,
      }).then((result) => {
        setSamples(result.samples ?? null);
      });
    }

    props.onChangeAction?.(action);
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.SAMPLES, {
      ...analyticsMetadata,
      itemIds: props.items.map(({id}) => id),
      numItemsSelected: props.items.length,
    });
  });

  const handleRequestSubmit = (): void => {
    if (action !== BulkUpdateSampleRolesAction.CLEAR && !useAlternateParties && parties.length === 0) {
      setPartiesError('Value is required');
    } else {
      setPartiesError(null);

      const analyticsMetadataForSubmit = {
        ...analyticsMetadata,
        ...(props.alternateParties != null &&
          !props.alternateParties.disabled && {
            modalMetadataEntryInfo: [
              {
                entryType: useAlternateParties
                  ? props.alternateParties.analyticsMetadataEntryType
                  : ModalMetadataEntryType.MANUAL,
                metadataElement: 'sample_control_role_party',
              },
            ],
          }),
        itemIds: selectedSampleIds,
        numItemsSelected: selectedSampleIds.length,
      };
      Analytics.trackEventWithModule(
        COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
        ANALYTICS_MODULE.SAMPLES,
        analyticsMetadataForSubmit
      );

      if (useAlternateParties) {
        props.onRequestSubmit(selectedItems, action, props.alternateParties!.partyIdsByItemId);
      } else {
        const partyIds = new Set(parties.map(({id}) => id));
        props.onRequestSubmit(
          selectedItems,
          action,
          objectFromEntries(selectedSampleIds.map((id) => [id, partyIds]))
        );
      }
    }
  };

  const handleRequestClose = (): void => {
    props.onRequestClose();
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.SAMPLES, {
      ...analyticsMetadata,
      itemIds: selectedSampleIds,
      numItemsSelected: selectedSampleIds.length,
    });
  };

  const handleChangeAction = (newAction: BulkUpdateSampleRolesAction): void => {
    setAction(newAction);
    props.onChangeAction?.(newAction);
  };

  const handleChangeParties = (newParties: Array<MinimalEntity>): void => {
    setParties(newParties);
    setPartiesError(null);
  };

  if (props.alternateParties != null) {
    assert(
      areSetsEqual(
        new Set(Object.keys(props.alternateParties.partyIdsByItemId)),
        new Set(props.items.map(({id}) => id))
      ),
      'Expected items in `alternateParties` to match `props.items`'
    );
  }

  const notice = props.getNoticeFn?.(selectedSampleIds);

  const maybeSampleStatusTable =
    props.shouldAlwaysShowSampleStatusTable && samples != null ? (
      <SampleStatusTable
        samples={samples}
        allowSelection={true}
        selectedSampleIds={selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        canSelectMultiple={true}
      />
    ) : null;

  return (
    <Modal
      title={`Update ${props.label}`}
      onRequestClose={handleRequestClose}
      buttons={[{text: 'Update', submit: true, disabled: selectedSampleIds.length === 0}]}
      onRequestSubmit={handleRequestSubmit}
    >
      {maybeSampleStatusTable}
      <FieldLabel label="Actions" labelStyle={FieldLabel.STYLES.LABEL} />
      <RadioButtonSelector
        options={[
          {label: `Add new ${props.label}`, value: BulkUpdateSampleRolesAction.ADD},
          {label: `Remove ${props.label}`, value: BulkUpdateSampleRolesAction.REMOVE},
          {
            label: `Replace current ${props.label} with new ones`,
            value: BulkUpdateSampleRolesAction.REPLACE,
          },
          {label: `Clear all ${props.label}`, value: BulkUpdateSampleRolesAction.CLEAR},
        ]}
        onChange={handleChangeAction}
        isVertical={true}
        selectedValue={action}
      />
      <CurrentAndNewValueRow
        label={props.label}
        currentValue={
          currentValues.length === 0 || selectedSampleIds.length === 0 ? (
            'None'
          ) : currentValues === MULTIPLE_VALUES ? (
            'Multiple values'
          ) : (
            <PartyList parties={currentValues} useAvatar={true} maxParties={3} />
          )
        }
        customNewValueInputLabel={
          action === BulkUpdateSampleRolesAction.REMOVE ? `${capitalize(props.label)} to remove` : undefined
        }
        newValueInput={
          action === BulkUpdateSampleRolesAction.CLEAR ? (
            <div id="bulk-update-scr-modal-new-value" className={css(styles.paddingTop5px)}>
              None
            </div>
          ) : props.alternateParties != null ? (
            <>
              <div>
                <label className="u-normal-font-weight">
                  <input
                    type="radio"
                    checked={useAlternateParties}
                    onChange={() => {
                      setUseAlternateParties(true);
                      setPartiesError(null);
                    }}
                    className={css(styles.radioInput)}
                    disabled={props.alternateParties.disabled}
                  />
                  <span
                    className={classNames('u-tiny-spacing-left', {
                      faded: props.alternateParties.disabled,
                    })}
                  >
                    {props.alternateParties.label}
                  </span>
                </label>
              </div>
              <div className="u-normal-font-weight u-flex-display u-vertical-flex">
                <label className="u-normal-font-weight">
                  <input
                    type="radio"
                    checked={!useAlternateParties}
                    onChange={() => {
                      setUseAlternateParties(false);
                    }}
                    className={css(styles.radioInput)}
                  />
                  <span className="u-tiny-spacing-left">Users or teams</span>
                </label>
                {!useAlternateParties && (
                  <div className="u-tiny-spacing-left u-flex-grow">
                    <EntityInputWithSearch
                      value={parties}
                      onChange={handleChangeParties}
                      size={TagsInputSize.SMALL}
                      shouldAllowMultiple={true}
                      allowTeams={true}
                      allowOrganizations={false}
                      allowBenchlingApps={props.allowBenchlingApps}
                      enableNewEntityInput={true}
                      className={classNames({
                        'fieldInput-input--error': partiesError != null,
                      })}
                      disabled={useAlternateParties}
                    />
                    {partiesError != null && <ErrorMessage error={partiesError} />}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <EntityInputWithSearch
                value={parties}
                onChange={handleChangeParties}
                size={TagsInputSize.SMALL}
                shouldAllowMultiple={true}
                allowTeams={true}
                allowOrganizations={false}
                allowBenchlingApps={props.allowBenchlingApps}
                enableNewEntityInput={true}
                className={classNames({'fieldInput-input--error': partiesError != null})}
              />
              {partiesError != null && <ErrorMessage error={partiesError} />}
            </>
          )
        }
      />
      {props.children}
      {props.warning != null && (
        <Row className={css(styles.paddingTop15px)}>
          <Col md={12}>
            <InlineBanner bannerType={InlineBannerType.WARNING} text={props.warning} />
          </Col>
        </Row>
      )}
      <Row className={classNames(css(styles.paddingTop15px), css(styles.labelFontSize))}>
        <Col md={12}>
          <HorizontalChipListField
            ids={[...new Set(selectedSampleIds)]}
            title="Selected items"
            chipsAreLinks={false}
          />
        </Col>
      </Row>
      {(notice?.header || notice?.content) && (
        <Row className={css(styles.paddingTop15px)}>
          <Col md={12}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text={notice.header}
              supportingInfo={notice.content}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
}

function determineCurrentValues(
  allPreviousValues: Array<Array<SampleRoleParty>>
): Array<SampleRoleParty> | typeof MULTIPLE_VALUES {
  if (allPreviousValues.length === 0) {
    // modal should never be opened with empty list of items, but just to be safe
    return MULTIPLE_VALUES;
  }

  const possibleCommonSetOfPreviousValues = new Set(allPreviousValues[0]);
  if (
    allPreviousValues.every((values) =>
      areSetsEqual(new Set(values), possibleCommonSetOfPreviousValues, isEqual)
    )
  ) {
    return allPreviousValues[0];
  }

  return MULTIPLE_VALUES;
}

export function computeNewPartyIds(
  action: BulkUpdateSampleRolesAction,
  currentPartyIds: Array<string>,
  actionPartyIds: Set<string>
): Array<string> {
  switch (action) {
    case BulkUpdateSampleRolesAction.ADD:
      return [...new Set([...currentPartyIds, ...actionPartyIds])];
    case BulkUpdateSampleRolesAction.REMOVE:
      return currentPartyIds.filter((id) => !actionPartyIds.has(id));
    case BulkUpdateSampleRolesAction.REPLACE:
      return [...actionPartyIds];
    case BulkUpdateSampleRolesAction.CLEAR:
      return [];
    default:
      assert(false, `Unexpected action ${action}`);
  }
}

const styles = StyleSheet.create({
  paddingTop5px: {paddingTop: '5px'},
  paddingTop15px: {paddingTop: '15px'},
  labelFontSize: {fontSize: '13px'},
  radioInput: {
    marginTop: '2px',
    verticalAlign: 'top',
  },
});
